import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { preloadAutoFollowSpacesState } from '@confluence/onboarding-helpers/entry-points/preloadAutoFollowSpacesState.ts';
import { preloadFeed } from '@confluence/feed/entry-points/preloadFeed.ts';
import { preloadPostOfficeConfluenceHomeBanner } from '@confluence/experiment-post-office-home-banner/entry-points/preloadConfuenceHomeBanner.ts';
import { preloadPersonalizedTemplates } from '@confluence/experiment-personalized-templates-v2/entry-points/preloadPersonalizedTemplates.ts';
import { preloadUseCasesNthUserOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadUseCasesNthUserOnboardingState.ts';
import { preloadHomeOnboardingTakeoverV2 } from '@confluence/onboarding-helpers/entry-points/preloadHomeOnboardingTakeoverV2.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { preloadPersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/preloadPersistentInviteEligible.ts';
import { fg } from '@confluence/feature-gating';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';

import { preloadTemplatesCollectionEligible } from '../../../../entry-points/preloadTemplatesCollectionEligible.ts';
import { preloadIntentBasedOnboarding } from '../../../../entry-points/preloadIntentBasedOnboarding.ts';
import { preloadSidePanel } from '../../preloadSidePanel.ts';
import { preloadWelcomeMessages } from '../../RightPanel/WelcomeMessage/preloadWelcomeMessages.ts';

import { preloadAgentsWaitingForYourInput } from './AgentSessions/preloadAgentsWaitingForYourInput.ts';
import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers.ts';
import { ALL_RECENT_ITEMS_COUNT_HOME_REFRESH } from './recentListConstants.ts';

const preloadRecentWork = (args: {
	cloudId: string;
	isLoggedIn: boolean;
	isWhiteboardViewedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
	isSlidesEnabled?: boolean;
	first?: number;
}) => {
	const queryOptions = getRecentWorkQueryOptions(args);

	if (queryOptions.skip) {
		return Promise.resolve({});
	}
	return queryAgg(queryOptions);
};

export const preloadForYou = ({
	userId,
	cloudId,
	isLicensed,
	isWhiteboardViewedEnabled = false,
	isDatabaseEnabled = false,
	isSlidesEnabled = false,
}: {
	userId: string | null;
	cloudId: string;
	isLicensed: boolean;
	isWhiteboardViewedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
	isSlidesEnabled?: boolean;
}): Promise<void | Error[] | (void | any[] | Error[] | {})[]> => {
	const preloadedSessionContext = getPreloaderFnContext();
	const { accessStatus } = preloadedSessionContext;
	const canAccessHome =
		accessStatus !== 'ANONYMOUS_ACCESS' && accessStatus !== 'UNLICENSED_AUTHENTICATED_ACCESS';

	const tasks = [
		preloadRecentWork({
			cloudId,
			isLoggedIn: !!userId,
			isWhiteboardViewedEnabled,
			isDatabaseEnabled,
			isSlidesEnabled,
			first: ALL_RECENT_ITEMS_COUNT_HOME_REFRESH,
		}),
		preloadFeed({
			isLoggedIn: !!userId,
		}),
		preloadSidePanel({
			isLicensed,
		}),
		preloadWelcomeMessages(),
	];

	if (userId) {
		tasks.push(preloadAgentsWaitingForYourInput({ cloudId, userId }));

		tasks.push(preloadIntentBasedOnboarding(), preloadTemplatesCollectionEligible());

		if (isLicensed && canAccessHome) {
			tasks.push(preloadAutoFollowSpacesState());

			tasks.push(preloadHomeOnboardingTakeoverV2());

			tasks.push(preloadUseCasesNthUserOnboardingState());
		}
	}

	if (isLicensed && canAccessHome && userId && accessStatus) {
		tasks.push(preloadPersonalizedTemplates(userId, accessStatus));
	}

	const unifiedSpacePreloader = preloadPersistentInviteButtonEligible(
		{ spaceKey: undefined },
		false,
	);

	if (unifiedSpacePreloader) {
		// @ts-ignore-next-line
		tasks.push(unifiedSpacePreloader); //type can be undefined, but the surrounding conditional prevents it
	}

	// https://switcheroo.atlassian.com/ui/gates/e8661ed8-caef-4eed-a747-bf4d58be15c9/key/post-office_enable-confluence-home-banner
	if (userId && canAccessHome && isLicensed && fg('post-office_enable-confluence-home-banner')) {
		// Confluence Home Banner in SSR
		const preloadPostOfficeHomeBanner = async () => {
			return SSRMeasures.run(
				'ssr-app/prepare/preloadQuery/postOffice:ConfluenceHomeBanner',
				async () => {
					const result = await preloadPostOfficeConfluenceHomeBanner();
					return result ?? {};
				},
			);
		};

		tasks.push(preloadPostOfficeHomeBanner());
	}

	return waitForAllReturnWhenError(tasks);
};
